import io, { Socket } from 'socket.io-client';
import { CHAT_API_BASE_URL, DISABLE_BACKEND_CHATBOT_CALLS } from './chatConfig';

interface ChatEventData {
  conversationId?: string;
  newState?: string;
  count?: number;
  [key: string]: unknown;
}

type ChatEventCallback = (data: ChatEventData) => void;

export class ChatWebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, ChatEventCallback[]> = new Map();

  connect(): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }
    // Chamadas ao backend-chatbot desativadas: não abre conexão WebSocket
    if (DISABLE_BACKEND_CHATBOT_CALLS) {
      return null as unknown as Socket;
    }

    this.socket = io(CHAT_API_BASE_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.socket.on('connect', () => {});
    this.socket.on('disconnect', () => {});
    this.socket.on('error', (error) => {});

    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  onNewClientMessage(callback: ChatEventCallback): void {
    this.addEventListener('new-client-message', callback);
  }

  onWaitingForHuman(callback: ChatEventCallback): void {
    this.addEventListener('waiting-for-human', callback);
  }

  onConversationAssumed(callback: ChatEventCallback): void {
    this.addEventListener('conversation-assumed', callback);
  }

  onStateChanged(callback: ChatEventCallback): void {
    this.addEventListener('state-changed', callback);
  }

  onMessage(callback: ChatEventCallback): void {
    this.addEventListener('message', callback);
  }

  onTyping(callback: ChatEventCallback): void {
    this.addEventListener('typing', callback);
  }

  onBotMessage(callback: ChatEventCallback): void {
    this.addEventListener('bot-message', callback);
  }

  onAdminMessage(callback: ChatEventCallback): void {
    this.addEventListener('admin-message', callback);
  }

  onWaitingCountUpdated(
    callback: (data: { count?: number; conversationId?: string }) => void
  ): void {
    this.addEventListener('waiting-count-updated', callback as ChatEventCallback);
  }

  onConversationStateChanged(callback: ChatEventCallback): void {
    this.addEventListener('conversation-state-changed', callback);
  }

  addEventListener(event: string, callback: ChatEventCallback): void {
    if (!this.socket) {
      return;
    }

    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);

    this.socket.on(event, callback);
  }

  removeEventListener(event: string, callback?: ChatEventCallback): void {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
      const listeners = this.listeners.get(event) || [];
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    } else {
      this.socket.off(event);
      this.listeners.delete(event);
    }
  }

  emit(event: string, data?: unknown): void {
    if (!this.socket) {
      return;
    }
    this.socket.emit(event, data);
  }
}

export const chatWebSocket = new ChatWebSocketService();
